import { PlatformLocation } from '@angular/common';
import { KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';

import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { ErrorId } from '../error-handler/error-id';
import { ActivityDescription, ActivityReason } from '../mru/models/activity-description.model';
import { RecommendedServiceV1 } from '../recommended/recommended-v1.service';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpPromise } from '../shared/instrumentation/instrumented-http.service';
import { AttachmentType, EmailReference } from '../shared/models/email-attachment.model';
import { FileItem, FileType, SourceProvenance } from '../shared/models/file-item.model';
import { HttpRequestOptions } from '../shared/models/http-request-options.model';
import { HttpResponseContentType } from '../shared/models/http-response-content-type.enum';
import { OperationStream } from '../shared/models/operation-stream.model';
import { MruConfigService } from '../shared/mru/mru-config.service';
import { MruDataItem } from '../shared/mru/mru-data-item.model';
import { MruData } from '../shared/mru/mru-data.service';
import { DocumentActivityType, MruPlusPlusV2Service } from '../shared/mru/mru-plus-plus-v2.service';
import { AccessTokenConsumer, AccessTokenService } from '../shared/services/access-token.service';
import { DateService } from '../shared/services/date.service';
import { OfficeHomeApiConfigService } from '../shared/services/officehome-api-config.service';
import { SchedulePriority, SchedulerService } from '../shared/services/scheduler.service';
import { UpdateContentService } from '../shared/services/update-content.service';
import { WindowService } from '../shared/services/window.service';
import { XhrService } from '../shared/services/xhr.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { ClientStartupConfig, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { LocService } from '../shared/shared-context/loc.service';
import { GuidGenerator } from '../shared/utilities/guid-generator.utility';
import { HtmlEscaper } from '../shared/utilities/html-escaper.utility';
import { ObservableHelper } from '../shared/utilities/observable-helper.utility';
import { ActionType, InsightName, PotentialAction, TaskInsight, User } from './models/myanalytics.model';
import { RawOfficeFeedUpcomingMeetingInfo } from './models/upcoming-meetings.model';
import { OfficeFeedDataService } from './officefeed-data.service';

type RawOfficeFeed = Array<RawOfficeFeedItem> | RawOfficeFeedNewEndpoint;
interface RawOfficeFeedNewEndpoint {
    items: Array<RawOfficeFeedItem>;
    name: string;
    requestId: string;
}

interface RawOfficeFeedItem {
    appInfo: RawOfficeFeedAppInfo;
    category: string;
    containerInfo: RawOfficeFeedContainerInfo;
    contentType: string;
    createdBy: RawOfficeFeedCreatedBy;
    createdDateTime: string;
    expiresAt?: string;
    feedbackId: string;
    file: RawOfficeFeedFileInfo;
    inclusionReasons: Array<RawOfficeFeedInclusionReason>;
    insightName?: InsightName;
    is_collab: boolean;
    itemGroups?: Array<string>;
    lastModifiedBy: any;
    lastModifiedDateTime: string;
    name: string;
    newsArticleInfo: RawOfficeFeedNewsArticleInfo;
    newsArticle: RawOfficeFeedNewsArticleInfo; // Same as newsArticleInfo?
    oneDriveItemInfo: RawOfficeFeedOneDriveItemInfo;
    potentialActions?: Array<PotentialAction>;
    taskInsight?: TaskInsight;
    upcomingMeetingInfo?: RawOfficeFeedUpcomingMeetingInfo;
    previewInfo: RawOfficeFeedPreviewInfo;
    resourceID: string;
    sharePointIds: RawOfficeFeedSharepointIds;
    size: number;
    subCategory: string;
    uri: string;
    video?: RawOfficeFeedStreamsVideo;
    webUrl: string;
}

interface RawOfficeFeedContainerInfo {
    webUrl: string;
    kind: string;
    displayName: string;
}

interface RawOfficeFeedCreatedBy {
    User: RawOfficeFeedNewsUser;
}

interface RawOfficeFeedNewsUser {
    DisplayName: string;
    Id?: string;
    email?: string;
}

interface RawOfficeFeedFileInfo {
    extension: string;
    mimeType: string;
    downloadUrl?: string;
}

interface RawOfficeFeedInclusionReason {
    actors: Array<RawOfficeFeedActor>;
    dateTime: string;
    inclusionKind: string;
    kind?: string;
    navigationId: string;
    reason: string;
    sharingMedium: string;
    sharingWebUrl: string;
    conversationSubject?: string;
}

interface RawOfficeFeedActor {
    User: RawOfficeFeedUser;
}

interface RawOfficeFeedOneDriveItemInfo {
    driveId: string;
    itemId: string;
}

interface RawOfficeFeedUser {
    dateTime: string;
    DisplayName: string;
    email: string;
}

interface RawOfficeFeedPreviewInfo {
    image?: string;
    imageUrl?: string;
    text?: string;
}

interface RawOfficeFeedSharepointIds {
    listId: string;
    listItemUniqueId: string;
    siteId: string;
    webId: string;
}

interface RawOfficeFeedNewsArticleInfo {
    byLine: string;
    firstPublishedDateTime: string;
    lastUpdatedDateTime: string;
}

interface RawOfficeFeedStreamsVideo {
    mediaDuration: string;
    // other attributes are currently not used
}

interface RawOfficeFeedAppInfo {
    iconUrl: string;
}

interface OfficeFeedInstrumentationBody {
    Actions: Array<OfficeFeedInstrumentationActionItem>;
}

interface OfficeFeedInstrumentationActionItem {
    FeedbackId: string;
    Action: string;
    DwellTimeMs?: number;
    ItemSource: string;
}

interface OfficeFeedActionItem {
    FeedbackId: string;
    Action: string;
    ActionPayload: string;
}

export class OfficeFeedV2Service extends XhrService implements OfficeFeedDataService {
    private readonly officeFeedDataStreams: Map<string, OperationStream<MruData>>;
    private readonly application: string;
    private readonly scenario: string;
    private readonly section: string;
    private readonly feedFlight: boolean;
    private readonly startupConfig: ClientStartupConfig;
    private readonly tamyDataStreamMap: Map<string, OperationStream<MruData>>;

    private readonly baseUrl: string;
    private static readonly locParam = 'rs=';
    private static readonly newSchema = 'forceNewSchema=';
    private static readonly e2eLatencyHeader = 'x-end2endlatencyms';
    private static readonly tamyPath = '/PeoplePredictionsB2/MicrosoftFeed';
    private static readonly tamyQueryParams = 'section=OfficeHomeTrending&scenario=OfficeHomeTrending';

    private fetchRequestIndex = 0;

    private readonly regExp: RegExp;

    constructor(
        accessTokenService: AccessTokenService,
        private readonly capabilitiesService: CapabilitiesService,
        contextService: ContextService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        private readonly locService: LocService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        private readonly mruConfigService: MruConfigService,
        platformLocation: PlatformLocation,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly updateContentService: UpdateContentService,
        scheduler: SchedulerService,
        private readonly dateService: DateService,
        private readonly windowService: WindowService
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, AccessTokenConsumer.Substrate, contextService, officeHomeApiConfigService, platformLocation, scheduler);
        const capabilities = this.capabilitiesService.capabilities;
        // TODO: Deprecate NTP
        this.scenario = capabilities.suggestedActionsEnabled ? 'OfficeSuggestedActions' :
            capabilities.pwaEnabled ? 'OfficeComApp' : 'OfficeComHome';
        this.section = capabilities.suggestedActionsEnabled ? 'OfficeSuggestedActions' : 'OfficeComHome';
        this.application = capabilities.pwaEnabled ? 'OfficeComApp' : 'OfficeComHome';

        this.baseUrl = (this.inMockMode ? this.contextService.sharedContext.officeHomeApiRoot :  `${this.mruConfigService.config.substrateApiEndpoint}`);

        this.officeFeedDataStreams = new Map<string, OperationStream<MruData>>();
        this.regExp = new RegExp('\{([0-9]+)\}');
        this.feedFlight = this.mruConfigService.config.officeFeedFlightEnabled;
        this.startupConfig = this.startupConfigService.config;

        // The feed team wants us to log this call to track latencies of a new endpoint
        this.tamyDataStreamMap = new Map<string, OperationStream<MruData>>();
    }

    public getOfficeFeed(): OperationStream<MruData> {
        let url = '';
        const mruConfig = this.mruConfigService.config;
        if (mruConfig.useFeedS2SApi) {
            url = `${this.contextService.sharedContext.officeHomeApiRoot}/api/officefeed?${OfficeFeedV2Service.locParam}${this.contextService.sharedContext.officeMarket}&feedFlight=${this.getFeedFlight()}`;
        } else {
            url = `${this.baseUrl}/PeoplePredictionsB2/MicrosoftFeed?section=${this.section}&${OfficeFeedV2Service.locParam}${this.contextService.sharedContext.officeMarket}&${OfficeFeedV2Service.newSchema}${this.mruConfigService.config.officeFeedNewSchemaEnabled}`;
        }

        const flights = this.startupConfig.sharedClientStartupContext.flights.toLowerCase();
        const feedFlights = [];
        const capabilities = this.capabilitiesService.capabilities;

        // we have to account for any configuration of 3 feed flights
        if (!!mruConfig.officeFeedFlightControlName && !!mruConfig.officeFeedFlightName) {
            if (flights.includes(mruConfig.officeFeedFlightControlName.toLowerCase())) {
                feedFlights.push(mruConfig.officeFeedExperimentControlName);
            } else if (flights.includes(mruConfig.officeFeedFlightName.toLowerCase())) {
                feedFlights.push(mruConfig.officeFeedExperimentName);
            }
        }
        if (!!mruConfig.officeFeedFlight2ControlName && !!mruConfig.officeFeedFlight2Name) {
            if (flights.includes(mruConfig.officeFeedFlight2ControlName.toLowerCase())) {
                feedFlights.push(mruConfig.officeFeedExperiment2ControlName);
            } else if (flights.includes(mruConfig.officeFeedFlight2Name.toLowerCase())) {
                feedFlights.push(mruConfig.officeFeedExperiment2Name);
            }
        }
        if (!!mruConfig.officeFeedFlight3ControlName && !!mruConfig.officeFeedFlight3Name) {
            if (flights.includes(mruConfig.officeFeedFlight3ControlName.toLowerCase())) {
                feedFlights.push(mruConfig.officeFeedExperiment3ControlName);
            } else if (flights.includes(mruConfig.officeFeedFlight3Name.toLowerCase())) {
                feedFlights.push(mruConfig.officeFeedExperiment3Name);
            }
        }

        if (!!capabilities.threeSRdlFeedExperiment) {
            feedFlights.push(capabilities.threeSRdlFeedExperiment);
        }

        if (feedFlights.length > 0) {
            url += `&experiment=${feedFlights.join('&experiment=')}`;
        }

        if (this.officeFeedDataStreams.has(url)) {
            return this.officeFeedDataStreams.get(url);
        }

        const updateStream = this.updateContentService.getOrAddUpdateStream(`officefeed-${url}`, () => this.fetchOfficeFeed(FetchStrategy.NetworkFirst, url, mruConfig.useFeedS2SApi));

        const recommendedFromNetwork = this.capabilitiesService.capabilities.prefetchDataEnabled ?
            this.prefetchOfficeFeed(url, mruConfig.useFeedS2SApi) : this.fetchOfficeFeed(FetchStrategy.NetworkFirst, url, mruConfig.useFeedS2SApi);

        const officeFeedDataStream = ObservableHelper.createResultsWithCacheObservable(
            this.fetchOfficeFeed(FetchStrategy.CacheOnly, url, mruConfig.useFeedS2SApi),
            recommendedFromNetwork,
            updateStream,
            _ => this.officeFeedDataStreams.delete(url) // clear the cached stream on failure so retry is possible
        );

        this.officeFeedDataStreams.set(url, officeFeedDataStream);

        return officeFeedDataStream;
    }

    public getTamyDocuments(count: number, types?: Set<FileType>): OperationStream<MruData> {
        const supportedTypes = this.mruConfigService.config.supportedFileTypes;
        const fileTypes = (types && types.size) ? supportedTypes.filter(type => types.has(FileItem.fileTypeNameToFileType(type))) : supportedTypes;
        const filterParams = fileTypes.reduce((acc, type, index) =>
            `${acc}${index > 0 ? ' or ' : ''}resourceVisualization/Type eq '${type}'`, '');
        const path = OfficeFeedV2Service.tamyPath;
        const queryParams = `$top=${count}&$filter=${encodeURIComponent(filterParams)}`; // TODO: These currently are not ported to TAMY but will be in the future
        const allQueryParams = `?${OfficeFeedV2Service.tamyQueryParams}&${queryParams}`;
        const trendingUrl = this.inMockMode ? `${this.contextService.sharedContext.officeHomeApiRoot}${path}/trending${allQueryParams}` : `${this.mruConfigService.config.substrateApiEndpoint}${path}${allQueryParams}`;

        //TODO: Skip new requests if sufficient data of desired types is present in cache
        if (this.tamyDataStreamMap.has(trendingUrl)) {
            return this.tamyDataStreamMap.get(trendingUrl);
        }

        const tamyDataFromCache = this.fetchOfficeFeed(FetchStrategy.CacheOnly, trendingUrl, false, SchedulePriority.Immediate, 'Trending', true, fileTypes);
        const tamyDataFromNetwork = this.fetchOfficeFeed(FetchStrategy.NetworkFirst, trendingUrl, false, SchedulePriority.Immediate, 'Trending', true, fileTypes);
        const tamyDataStream = ObservableHelper.createResultsWithCacheObservable(
            tamyDataFromCache,
            tamyDataFromNetwork,
            null,
            _ => this.tamyDataStreamMap.delete(trendingUrl)
        );

        this.tamyDataStreamMap.set(trendingUrl, tamyDataStream);

        return tamyDataStream;
    }

    public logInstrumentation(feedbackIds: Array<string>, actions: Array<string>, itemSource: Array<string>, target?: Array<string>) {
        return new Promise<void>((resolve, reject) => {
            const url = `${this.inMockMode ? this.contextService.sharedContext.officeHomeApiRoot : this.mruConfigService.config.substrateApiEndpoint}/peoplepredictionsb2/Feedback`;
            const actionsArray = [];
            for (let i = 0; i < feedbackIds.length; i++ ) {
                actionsArray.push({
                    FeedbackId: feedbackIds[i],
                    Action: actions[i],
                    ItemSource: itemSource[i],
                    Target: target ? target[i] : '',
                    Timestamp: new Date(Date.now()).toISOString()
                });
            }
            const instrumentationBody: OfficeFeedInstrumentationBody = {
                Actions: actionsArray
            };

            const promiseCallback = (promise: InstrumentedHttpPromise<boolean>) => {
                promise
                    .instrument((result, params) => {
                        params[InstrumentationParam.ItemCategory] = actionsArray.map(item => item.Action).join(',');
                        params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                    })
                    .then(() => resolve(null));
            };

            const requestConfig = this.createRequestConfig(this.startupConfig.sharedClientStartupContext.correlationId, 'Feedback');

            this.post(
                KnownInstrumentationEvents.OfficeFeedInstrumentation,
                KnownRequestInstrumentationEvents.OfficeFeedInstrumentation,
                url,
                instrumentationBody,
                promiseCallback,
                reject,
                requestConfig,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                this.mruConfigService.config.deferFeedWrite ? SchedulePriority.VeryLow : SchedulePriority.Immediate
            );
        });
    }

    public performAction(feedbackId: string, action: string, actionPayload: string): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            const url = `${this.inMockMode ? this.contextService.sharedContext.officeHomeApiRoot : this.mruConfigService.config.substrateApiEndpoint}/peoplepredictionsb2/v2/myanalyticsaction`;
            const actionBody: OfficeFeedActionItem = {
                FeedbackId: feedbackId,
                Action: action,
                ActionPayload: actionPayload
            };

            const promiseCallback = (promise: InstrumentedHttpPromise<boolean>) => {
                promise
                    .instrument((_, params) => {
                        params[InstrumentationParam.ItemCategory] = actionBody.Action;
                    })
                    .then(() => resolve(true));
            };

            const requestConfig = this.createRequestConfig(this.startupConfigService.config.sharedClientStartupContext.correlationId, 'Feedback');

            this.post(
                KnownInstrumentationEvents.OfficeFeedAction,
                KnownRequestInstrumentationEvents.OfficeFeedAction,
                url,
                actionBody,
                promiseCallback,
                reject,
                requestConfig,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                SchedulePriority.Immediate
            );
        });
    }

    public getStreamsThumbnail(url: string): Promise<string> {
        if (this.inMockMode) {
            url = `${this.contextService.sharedContext.officeHomeApiRoot}/StreamsThumbnail`;
        }
        const reader = new FileReader();

        return new Promise<string>((resolve, reject) => {
            const promiseCallback = (promise: InstrumentedHttpPromise<Blob>) => {
                promise
                    .then(data => {
                        if (data && data.data) {
                            reader.onloadend = () => {
                                const result = (reader.result && typeof reader.result === 'string' && reader.result) || '';
                                if (result.length > 0) {
                                    resolve(result);
                                } else {
                                    resolve(null);
                                }
                            };
                            reader.onerror = () => resolve(null);
                            reader.onabort = () => resolve(null);
                            reader.readAsDataURL(data.data);
                        } else {
                            resolve(null);
                        }
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.AfEUN);
                    });
            };

            const requestConfig = this.createRequestConfig(this.startupConfig.sharedClientStartupContext.correlationId, 'StreamsThumbnail', HttpResponseContentType.Blob);
            this.get<Blob>(
                KnownInstrumentationEvents.OfficeFeedStreamThumbnailRequest,
                KnownRequestInstrumentationEvents.OfficeFeedStreamThumbnailRequest,
                url,
                promiseCallback,
                reject,
                requestConfig,
                FetchStrategy.NetworkOnly
            );
        });
    }

    protected handleQueryParam(paramNameInLowerCase: string, value: string | Array<string>, requestConfig: HttpRequestOptions) {
        if (paramNameInLowerCase === 'mockofficefeed' && this.mockRequestHeader) {
            requestConfig.headers.set(this.mockRequestHeader, value);
        }
    }

    private prefetchOfficeFeed(url: string, useFeedS2SApi: boolean): Promise<MruData> {
        if (this.windowService.nativeWindow.prefetchRecommendedDataPromise) {
            return this.windowService.nativeWindow.prefetchRecommendedDataPromise.then((data: RawOfficeFeed & {recommendedRequestId: string}) => {
                if (!data) {
                    return Promise.reject('Prefetch failed');
                }
                const dataItems = (this.mruConfigService.config.officeFeedNewSchemaEnabled ? (data as RawOfficeFeedNewEndpoint).items : data as Array<RawOfficeFeedItem>) || [];
                const items: Array<MruDataItem> = dataItems.map(doc => this.convertItemToMruDataItem(doc));
                const validItems = items.filter(doc => doc && doc.Activity?.formatString);
                return {
                    items: validItems,
                    impressionId: data.recommendedRequestId || '',
                    fromCache: false
                };
            });
        } else {
            return this.fetchOfficeFeed(FetchStrategy.NetworkFirst, url, useFeedS2SApi);
        }
    }

    private fetchOfficeFeed(strategy: FetchStrategy, url: string, useFeedS2SApi: boolean, priority?: SchedulePriority, scenarioTag?: string, isTrending?: boolean, fileTypes?: Array<string>): Promise<MruData> {
        return new Promise<MruData>((resolve, reject) => {
            const requestId = GuidGenerator.createGuid();
            const requestConfig = this.createRequestConfig(requestId, scenarioTag || 'MicrosoftFeed');
            const isSPNF = scenarioTag === 'MicrosoftFeedSPNF';
            const promiseCallback = (promise: InstrumentedHttpPromise<RawOfficeFeed>) => {
                promise
                    .instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                        params[InstrumentationParam.Index] = (this.fetchRequestIndex++).toString();
                        params[InstrumentationParam.ImpressionId] = (!!result.headers && result.headers.get(RecommendedServiceV1.clientRequestIdHeader)) || requestId;
                        params[InstrumentationParam.ApiLatency] = (!!result.headers && result.headers.get(OfficeFeedV2Service.e2eLatencyHeader) || -1).toString();
                        if (result.ok && result.data && !isSPNF) {
                            const items: Array<RawOfficeFeedItem> = this.mruConfigService.config.officeFeedNewSchemaEnabled ? (result.data as RawOfficeFeedNewEndpoint).items : result.data as Array<RawOfficeFeedItem>;
                            if (items) {
                                const itemsCount = items.length;
                                params[InstrumentationParam.ResourceIdDistribution] = items.map(d =>
                                    d.sharePointIds && d.sharePointIds.listItemUniqueId ? d.sharePointIds.listItemUniqueId : '').toString();
                                params[InstrumentationParam.OfficeFeedCategoryDistribution] = items.map(d => d.category).toString();
                                params[InstrumentationParam.ActualSize] = itemsCount.toString();
                            }
                        } else if (strategy === FetchStrategy.CacheOnly && result.status === 401) {
                            params[InstrumentationParam.OfficeFeedUrl] = InstrumentationService.filterPii(url);
                            params[InstrumentationParam.OfficeFeedAuthenticateHeader] = result.headers.get('WWW-Authenticate');
                        }
                    })
                    .then(data => {
                        if (isSPNF) {
                            resolve(null);
                        } else {
                            let rawItems: Array<RawOfficeFeedItem> = data && data.data && (this.mruConfigService.config.officeFeedNewSchemaEnabled ? (data.data as RawOfficeFeedNewEndpoint).items : data.data as Array<RawOfficeFeedItem>) || [];
                            if (fileTypes && fileTypes.length > 0) {
                                rawItems = rawItems.filter(item => fileTypes.includes(item.subCategory));
                            }
                            const items: Array<MruDataItem> = rawItems.map(doc => this.convertItemToMruDataItem(doc, isTrending));
                            const validItems = isTrending ? items : items.filter(doc => doc && doc.Activity?.formatString); // don't check activity for trending items
                            resolve({
                                items: validItems,
                                impressionId: !!data.headers && data.headers.get(RecommendedServiceV1.clientRequestIdHeader) || requestId,
                                fromCache: strategy === FetchStrategy.CacheOnly
                            });
                        }
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.AfEUN);
                    });
            };

            this.get<RawOfficeFeed>(
                isTrending ? KnownInstrumentationEvents.TrendingAroundMeRequest : KnownInstrumentationEvents.OfficeFeedRequest,
                isTrending ? KnownRequestInstrumentationEvents.TrendingAroundMeRequest : KnownRequestInstrumentationEvents.OfficeFeedRequest,
                url,
                promiseCallback,
                reject,
                requestConfig,
                strategy,
                null,
                null,
                null,
                useFeedS2SApi ? AccessTokenConsumer.OfficeHome : AccessTokenConsumer.Substrate,
                null,
                priority
            );
        });
    }

    private createRequestConfig(requestId: string, endpoint: string, responseType?: HttpResponseContentType): HttpRequestOptions {
        const headers = new Map<string, string | Array<string>>([
            ['ScenarioTag', `${this.scenario}${endpoint}`],
            ['Content-Type', 'application/json'],
            ['X-OfficeFeedFlight', this.getFeedFlight()],
            ['X-AnchorMailbox', `Oid:${this.startupConfig.objectId}@${this.startupConfig.tenantId}`],
            ['X-Application', this.application],
            ['X-ApplicationScenario', this.scenario],
            ['X-RoutingParameter-SessionKey', this.startupConfig.objectId],
            [RecommendedServiceV1.clientRequestIdHeader, requestId]
        ]);
        const requestOptions: HttpRequestOptions = {
            headers
        };
        if (responseType) {
            requestOptions.responseType = responseType;
        }
        return requestOptions;
    }

    private getFeedFlight(): string {
        return this.feedFlight ? 'experiment' : 'evaluation';
    }

    private convertItemToMruDataItem(officeFeedItem: RawOfficeFeedItem, isTrending: boolean = false): MruDataItem {
        if (officeFeedItem.category.toLowerCase() === 'file' || officeFeedItem.category.toLowerCase() === 'video') {
            return this.convertFileToMruDataItem(officeFeedItem, isTrending);
        } else if (officeFeedItem.category.toLowerCase() === 'newsarticle') {
            return this.convertNewsItemToMruDataItem(officeFeedItem);
        } else if (officeFeedItem.category.toLowerCase() === 'upcomingmeeting' && this.mruConfigService.config.upcomingMeetingEnabled) {
            return this.convertUpcomingMeetingToMruDataItem(officeFeedItem);
        } else if (officeFeedItem.category.toLowerCase() === 'myanalyticsinsight' && this.mruConfigService.config.myAnalyticsEnabled) {
            return this.convertMyAnalyticsToMruDataItem(officeFeedItem);
        }
        return null;
    }

    private convertMyAnalyticsToMruDataItem(officeFeedMyAnalytics: RawOfficeFeedItem): MruDataItem {
        const inclusionReason = officeFeedMyAnalytics.inclusionReasons && officeFeedMyAnalytics.inclusionReasons[0];
        const activityUser = inclusionReason.actors[0]?.User || {} as User;
        const byLinePlain = activityUser.DisplayName;
        const byLineFormatted = byLinePlain;
        const activityDescription: ActivityDescription = {
            formatString: byLineFormatted,
            plainText: byLinePlain,
            people: [{
                    upn: activityUser.email,
                    tag: 'c0',
                    name: activityUser.DisplayName
                }],
            reasonType: ActivityReason.MyAnalytics,
            timestamp: officeFeedMyAnalytics.createdDateTime ? new Date(officeFeedMyAnalytics.createdDateTime) : null
        };
        const descriptionText = '"' + officeFeedMyAnalytics.previewInfo?.text + '"';
        const itemTimestamp = officeFeedMyAnalytics.lastModifiedDateTime ? new Date(officeFeedMyAnalytics.lastModifiedDateTime) : null;
        return {
            Application: null,
            Category: officeFeedMyAnalytics.category,
            DocId: null,
            DriveId: null,
            FileName: officeFeedMyAnalytics.name,
            Title: officeFeedMyAnalytics.name,
            Url: officeFeedMyAnalytics.webUrl,
            WebUrl: officeFeedMyAnalytics.webUrl,
            LastAccessedTime: itemTimestamp,
            FileType: 'MyAnalytics',
            PathArray: null,
            Provenance: SourceProvenance[SourceProvenance.Substrate],
            ResourceId: '',
            UrlAlreadyEncoded: true,
            Activity: activityDescription,
            ThumbnailUrl: null,
            FeedbackId: officeFeedMyAnalytics.feedbackId,
            TaskInsight: officeFeedMyAnalytics.taskInsight,
            PotentialAction: officeFeedMyAnalytics.potentialActions,
            Actor: activityUser,
            InsightName: officeFeedMyAnalytics.insightName,
            InsightDescription: descriptionText,
            InsightDescriptionPrefix: this.getInsightDescriptionPrefix(officeFeedMyAnalytics.taskInsight?.actionType)
        };
    }

    private getInsightDescriptionPrefix(actionType: ActionType): string {

        let insightDescriptionPrefix = this.locService.YouSaid;
        if (actionType === ActionType.FollowUp || actionType === ActionType.FollowUpActionRequest || actionType === ActionType.FollowUpReplyRequest) {
            insightDescriptionPrefix = this.locService.YouAsked;
        } else if (actionType === ActionType.Commitment) {
            insightDescriptionPrefix = this.locService.YouSaid;
        } else if (actionType === ActionType.Request) {
            insightDescriptionPrefix = this.locService.TheyAsked;
        }
        return insightDescriptionPrefix;
    }

    private convertUpcomingMeetingToMruDataItem(officeFeedUpcomingMeeting: RawOfficeFeedItem): MruDataItem {
        const upcomingMeetingInfo = officeFeedUpcomingMeeting.upcomingMeetingInfo;
        const itemTimestamp = new Date(upcomingMeetingInfo.start) ??  null;
        const timespan = this.dateService.timespanInSeconds(itemTimestamp);
        const isTeamsMeeting = upcomingMeetingInfo.onlineMeeting?.onlineMeetingProvider === 'TeamsForBusiness';

        // Do not add Upcoming meeting as recommended card if timestamp is beyond the threshold
        const expiresAt = new Date(officeFeedUpcomingMeeting.expiresAt) ?? null;
        const currentTime = new Date();
        const isUpcomingMeetingAccepted = officeFeedUpcomingMeeting.itemGroups && officeFeedUpcomingMeeting.itemGroups.length > 0 && officeFeedUpcomingMeeting.itemGroups.indexOf('MeetingOnlineAttending') !== -1;

        if (!this.inMockMode && expiresAt && currentTime > expiresAt) {
            return null;
        }

        const hasMeetingStarted = timespan > 0;
        let byLineInputString = hasMeetingStarted ? this.locService.OfficeFeedYourUpcomingMeetingStarted : this.locService.OfficeFeedYourUpcomingMeeting;

        // if meeting starts within a minute, show Happening now text.
        if (!hasMeetingStarted && timespan > -60) {
            byLineInputString = this.locService.OfficeFeedYourUpcomingMeetingHappeningNow;
        }

        const joinMeetingText = isTeamsMeeting ? this.locService.JoinTeamsMeeting : this.locService.SuggestedActionJoinMeeting;
        const activityDescription: ActivityDescription = {
            formatString: byLineInputString,
            plainText: byLineInputString,
            people: null,
            reasonType: ActivityReason.Meeting,
            timestamp: new Date(upcomingMeetingInfo.start) ?? null
        };

        return {
            Application: null,
            Category: officeFeedUpcomingMeeting.category,
            DocId: null,
            DriveId: null,
            FileName: null,
            Title: officeFeedUpcomingMeeting.previewInfo?.text,
            Url: officeFeedUpcomingMeeting.webUrl,
            WebUrl: officeFeedUpcomingMeeting.webUrl,
            LastAccessedTime: itemTimestamp,
            FileType: 'UpcomingMeeting',
            PathArray: null,
            Provenance: SourceProvenance[SourceProvenance.Substrate],
            UrlAlreadyEncoded: true,
            Activity: activityDescription,
            FeedbackId: officeFeedUpcomingMeeting.feedbackId,
            Attendees: upcomingMeetingInfo.attendees,
            Subject: upcomingMeetingInfo.subject,
            JoinUrl: upcomingMeetingInfo.onlineMeeting?.joinUrl,
            MeetingStart: this.locService.formatTimeFromDate(new Date(upcomingMeetingInfo.start)),
            MeetingEnd: this.locService.formatTimeFromDate(new Date(upcomingMeetingInfo.end)),
            Location: upcomingMeetingInfo.location?.displayName,
            Organizer: upcomingMeetingInfo.organizer,
            JoinMeetingText: joinMeetingText,
            IsUpcomingMeetingAccepted: isUpcomingMeetingAccepted,
            IsTeamsMeeting: isTeamsMeeting
        };
    }

    private convertNewsItemToMruDataItem(officeFeedNewsArticle: RawOfficeFeedItem): MruDataItem {
        const byLinePlain = this.locService.format(this.locService.OfficeFeedPostedBy, (officeFeedNewsArticle.newsArticleInfo && officeFeedNewsArticle.newsArticleInfo.byLine || ''));
        const byLineFormatted = this.locService.format(this.locService.OfficeFeedPostedBy, `<c0>${officeFeedNewsArticle.newsArticleInfo && officeFeedNewsArticle.newsArticleInfo.byLine || ''}</c0>`);
        const newsArticleUser = officeFeedNewsArticle.createdBy && officeFeedNewsArticle.createdBy.User || {} as RawOfficeFeedNewsUser;
        const activityDescription: ActivityDescription = {
            formatString: byLineFormatted,
            plainText: byLinePlain,
            people: [{
                    upn: newsArticleUser.Id,
                    tag: 'c0',
                    name: newsArticleUser.DisplayName
                }],
            reasonType: ActivityReason.News, // TODO: update when teams figure out reasontypes
            timestamp: officeFeedNewsArticle.newsArticleInfo.firstPublishedDateTime ? new Date(officeFeedNewsArticle.newsArticleInfo.firstPublishedDateTime) : null
        };
        const itemTimestamp = officeFeedNewsArticle.lastModifiedDateTime ? new Date(officeFeedNewsArticle.lastModifiedDateTime) : null;
        return {
            Application: null,
            Category: officeFeedNewsArticle.category,
            DocId: officeFeedNewsArticle.oneDriveItemInfo && officeFeedNewsArticle.oneDriveItemInfo.itemId,
            DriveId: officeFeedNewsArticle.oneDriveItemInfo && officeFeedNewsArticle.oneDriveItemInfo.driveId,
            FileName: officeFeedNewsArticle.name,
            Title: officeFeedNewsArticle.name,
            Url: officeFeedNewsArticle.uri,
            WebUrl: officeFeedNewsArticle.webUrl,
            LastAccessedTime: itemTimestamp,
            FileType: 'NewsArticle',
            PathArray: officeFeedNewsArticle.containerInfo ? [officeFeedNewsArticle.containerInfo.displayName] : null, // News article is always from sharepoint site, so check for container kind is not required here.
            Provenance: SourceProvenance[SourceProvenance.Substrate],
            ResourceId: officeFeedNewsArticle.sharePointIds ? officeFeedNewsArticle.sharePointIds.listItemUniqueId : '',
            UrlAlreadyEncoded: true,
            Activity: activityDescription, // TODO: figure out with team
            ThumbnailUrl: officeFeedNewsArticle.previewInfo?.image,
            FeedbackId: officeFeedNewsArticle.feedbackId
        };
    }

    private convertOfficeFeedActivityToActivityDescription(activity: RawOfficeFeedInclusionReason): ActivityDescription {
        let formatString = activity.reason || '';
        let match = null;
        while ((match = this.regExp.exec(formatString)) !== null) {
            const i = match[1];
            // Passing in a function to replace to bypass special replacement patterns. See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/replace#Specifying_a_string_as_a_parameter
            formatString = formatString.replace(match[0], () => `<c${i}>${activity.actors[i].User.DisplayName}</c${i}>`);
        }
        return {
            formatString,
            plainText: HtmlEscaper.stripTags(formatString),
            people: (activity.actors || []).map(function (user, index) {
                return {
                    upn: user && user.User.email,
                    tag: `c${index}`,
                    name: user.User.DisplayName
                };
            }),
            reasonType: activity.kind === 'popularVideoInclusionReason' ? ActivityReason.Trending : activity.kind === 'meetingRecordingInclusionReason' ? ActivityReason.Video : this.convertReasonTypeToActivityReason(activity.inclusionKind), // TODO: ask feed team to send this activity the same way as other activity
            timestamp: activity.dateTime ? new Date(activity.dateTime) : null
        };
    }

    private convertReasonTypeToActivityReason(type: string) {
        const enumType = DocumentActivityType[type];
        return MruPlusPlusV2Service.convertMruReasonTypeToActivityReason(enumType);
    }

    private convertFileToMruDataItem(officeFeedFile: RawOfficeFeedItem, isTrending: boolean = false): MruDataItem {
        const itemTimestamp = officeFeedFile.lastModifiedDateTime ? new Date(officeFeedFile.lastModifiedDateTime) : null;
        const sharepointIds = officeFeedFile.sharePointIds;
        const sharepointInfo = sharepointIds && {
            list_id: sharepointIds.listId,
            list_item_unique_id: sharepointIds.listItemUniqueId,
            site_id: sharepointIds.siteId,
            site_url: null,
            web_id: sharepointIds.webId
        };

        const inclusionReason = officeFeedFile.inclusionReasons && officeFeedFile.inclusionReasons[0];
        let teamsDeepLink = undefined;
        let navigationId = undefined;
        let activityDescription: ActivityDescription = undefined;
        let isEmailAttachment = false;
        let emailReference: EmailReference = null;
        if (inclusionReason && !isTrending) {
            activityDescription = this.convertOfficeFeedActivityToActivityDescription(inclusionReason);
            isEmailAttachment = inclusionReason.inclusionKind === 'EmailAttachment';
            if (this.capabilitiesService.capabilities.recommendedTeamsLinkEnabled) {
                teamsDeepLink = inclusionReason.sharingWebUrl;
            }
            if (this.capabilitiesService.capabilities.recommendedDeepLinkEnabled) {
                navigationId = inclusionReason.navigationId;
            }
            if (isEmailAttachment) {
                emailReference = {
                    AttachmentType: AttachmentType.Classic,
                    ConversationId: '',
                    Subject: inclusionReason.conversationSubject || ''
                };
            }

        }
        let useWebUrl = isEmailAttachment;
        const fileType = this.fileType(officeFeedFile);
        if (fileType === FileType.MeetingNotes || fileType === FileType.Whiteboard) {
            useWebUrl = true;
        }
        const category = officeFeedFile.category.toLowerCase() === 'video' ? 'StreamsVideo' : 'Document';
        const mediaDuration = category === 'StreamsVideo' ? officeFeedFile.video?.mediaDuration : undefined;
        const thumbnailUrl = category === 'StreamsVideo' || officeFeedFile.subCategory === 'Custom' ? officeFeedFile.previewInfo.image : undefined;
        const url = useWebUrl ? officeFeedFile.webUrl : officeFeedFile.uri;
        return {
            Application: FileType[fileType],
            Category: category,
            DocId: officeFeedFile.oneDriveItemInfo && officeFeedFile.oneDriveItemInfo.itemId,
            DriveId: officeFeedFile.oneDriveItemInfo && officeFeedFile.oneDriveItemInfo.driveId,
            Extension: officeFeedFile?.file?.extension || FileItem.getFileExtensionFromUrl(url),
            FileName: officeFeedFile.name,
            Title: officeFeedFile.name,
            Url: url,
            WebUrl: officeFeedFile.webUrl,
            LastAccessedTime: itemTimestamp,
            FileType: FileType[fileType],
            PathArray: this.getPathArray(officeFeedFile.containerInfo?.kind, officeFeedFile.containerInfo?.displayName) ?? null,
            Provenance: SourceProvenance[SourceProvenance.Substrate],
            ResourceId: officeFeedFile.sharePointIds ? officeFeedFile.sharePointIds.listItemUniqueId : '',
            UrlAlreadyEncoded: true,
            Activity: activityDescription, // TODO: figure out with team
            SharepointInfo: sharepointInfo,
            IsEmailAttachment: isEmailAttachment,
            EmailReference: emailReference,
            IsCollab: officeFeedFile.is_collab,
            FeedbackId: officeFeedFile.feedbackId,
            NavigationId: navigationId,
            TeamsDeepLink: teamsDeepLink,
            ThumbnailUrl: thumbnailUrl,
            MediaDuration: mediaDuration,
            IconUrl: officeFeedFile.appInfo?.iconUrl
        };
    }

    private fileType(item: RawOfficeFeedItem): FileType {
        let fileType = FileItem.fileTypeNameToFileType(item.subCategory);

        if (!fileType || fileType === FileType.None) {
            fileType = FileType.Unknown;
        } else if (fileType === FileType.OneNote && item.file?.extension === 'note') {
            // Detect meeting notes using extension
            fileType = FileType.MeetingNotes;
        }
        return fileType;
    }

    private getPathArray(kind: string, displayName: string): Array<string> {
        if (!displayName) {
            return null;
        }
        if (this.isOneDrive(kind) && displayName) {
            return [this.locService.format(this.locService.EdgeworthFilePathTitle, displayName)];
        }
        return [displayName];
    }

    private isOneDrive(kind: string) {
        const containerType = kind?.toLocaleLowerCase();
        return containerType === 'onedrive' || containerType === 'onedrivebusiness';
    }
}
